import React, { useState, useEffect } from 'react';
import { fetchGalleryItems, type GalleryItem } from '../lib/gallery-client';

// ── Hardcoded fallback data (shown immediately; replaced if API succeeds) ──────

const FALLBACK_REPAIRS: GalleryItem[] = [
  {
    id: 'f-r-1', category: 'repairs', title: 'PS5', subtitle: 'Overheating - APU Oxidation',
    caption: 'PS5 suffering from severe overheating caused by oxidation spots on the APU and heat sink. We dont just swish around the existing liquid metal, we completely remove the liquid metal, and clean & polish the 2 surfaces before reapplying liquid metal.',
    image_url: '/img_6822.jpg', secondary_image_url: '/img_6815.jpg',
    storage_path: null, secondary_storage_path: null, mime_type: 'image/jpeg',
    sort_order: 0, visible: true, featured: true, created_at: '', updated_at: '',
  },
  {
    id: 'f-r-2', category: 'repairs', title: 'PS5', subtitle: 'Deep Cleaning',
    caption: 'Professional deep cleaning service. We fully disassemble every console to remove dust buildup from fans, heatsinks, and internal components that cause overheating and performance issues.',
    image_url: '/img_7431.jpg', secondary_image_url: '/img_7430.jpg',
    storage_path: null, secondary_storage_path: null, mime_type: 'image/jpeg',
    sort_order: 1, visible: true, featured: false, created_at: '', updated_at: '',
  },
  {
    id: 'f-r-3', category: 'repairs', title: 'PS5', subtitle: 'Thermal Maintenance',
    caption: 'Comprehensive thermal maintenance including heatsink cleaning, surface polishing, and fresh thermal compound application to ensure optimal cooling performance.',
    image_url: '/img_7432.jpg', secondary_image_url: '/img_7431.jpg',
    storage_path: null, secondary_storage_path: null, mime_type: 'image/jpeg',
    sort_order: 2, visible: true, featured: false, created_at: '', updated_at: '',
  },
];

const FALLBACK_CONTROLLERS: GalleryItem[] = [
  ['Iron Man Edition',  '/img_3358.jpeg', 'Red and gold chrome PS5 controller with premium metallic finish', true],
  ['Arctic Silver',     '/img_3363.jpeg', 'Silver and white PS5 controller with gray gradient sides', false],
  ['Gold Prestige',     '/img_3368.jpeg', 'Gold and black PS5 controller with carbon fiber touchpad', false],
  ['Gunmetal Pro',      '/img_3374.jpeg', 'Gray PS5 controller with red button accents', false],
  ['Super Station',     '/img_3330.jpeg', 'Custom controller with colorful buttons and unique design', false],
  ['Purple Carbon',     '/img_3347.jpeg', 'Purple carbon fiber PS5 controller with premium texture', false],
  ['Hex Elite',         '/img_3218.jpeg', 'Back paddle upgrade with adaptive triggers - go from traditional triggers to hair click triggers', false],
  ['Blue Phantom',      '/img_3438.jpeg', 'Transparent blue controller with carbon fiber touchpad', false],
  ['Urban Camo',        '/img_3481.jpeg', 'Gray camouflage pattern with textured finish', false],
  ['Stealth Black',     '/img_3248.jpeg', 'All-black controller with premium matte finish', false],
  ['Crimson Elite',     '/img_3529.jpeg', 'Red and black controller with chrome accents', false],
  ['Ocean Blue',        '/img_3534.jpeg', 'Blue controller with textured grip design', false],
  ['Sunset Orange',     '/img_3540.jpeg', 'Orange and white custom controller', false],
  ['Forest Green',      '/img_3545.jpeg', 'Green controller with unique button layout', false],
  ['Rose Gold',         '/img_3552.jpeg', 'Rose gold controller with premium metallic finish', false],
  ['Midnight Carbon',   '/img_7067.jpg',  'Carbon fiber pattern with LED accents', false],
  ['Custom Build',      '/img_8232.jpg',  'Custom controller with unique design and finish', false],
  ['Premium Edition',   '/img_8233.jpg',  'Premium custom controller with specialized modifications', false],
].map(([title, image_url, caption, featured], i) => ({
  id: `f-c-${i}`, category: 'controllers' as const, title: title as string,
  caption: caption as string, subtitle: '', image_url: image_url as string,
  secondary_image_url: '', storage_path: null, secondary_storage_path: null,
  mime_type: 'image/jpeg', sort_order: i, visible: true, featured: featured as boolean,
  created_at: '', updated_at: '',
}));

const FALLBACK_STRANGE: GalleryItem[] = [
  {
    id: 'f-s-1', category: 'strange', title: 'Spider in HDMI Port',
    caption: "An actual spider somehow found its way into this HDMI port. One of the strangest discoveries we've made during a repair - nature finds a way into the most unexpected places.",
    subtitle: '', image_url: '/ps5hdmi_spider_photo.jpg', secondary_image_url: '',
    storage_path: null, secondary_storage_path: null, mime_type: 'image/jpeg',
    sort_order: 0, visible: true, featured: true, created_at: '', updated_at: '',
  },
  {
    id: 'f-s-2', category: 'strange', title: 'Melted Cable',
    caption: 'Switch OLED with a melted charging cable - a rare but serious electrical hazard caused by damaged wiring or improper charging conditions.',
    subtitle: '', image_url: '/ps5hdmi_spider_photo.jpg', secondary_image_url: '',
    storage_path: null, secondary_storage_path: null, mime_type: 'image/jpeg',
    sort_order: 1, visible: true, featured: false, created_at: '', updated_at: '',
  },
];

// ── Sub-components ─────────────────────────────────────────────────────────────

function GalleryImg({ src, alt, className = '' }: { src: string; alt: string; className?: string }) {
  const [errored, setErrored] = useState(false);
  if (errored || !src) {
    return (
      <div className={`flex items-center justify-center bg-[#1A1A1A] text-gray-700 text-xs ${className}`}>
        No image
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setErrored(true)}
      className={`w-full h-full object-cover ${className}`}
    />
  );
}

function RepairCard({ item }: { item: GalleryItem }) {
  return (
    <div className="bg-[#1A1A1A] rounded-lg overflow-hidden border border-gray-800 hover:border-red-900 transition-all">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-2xl font-bold text-white">{item.title}</h3>
          {item.subtitle && (
            <span className="px-3 py-1 bg-red-900/20 text-red-500 rounded-full text-sm font-medium">
              {item.subtitle}
            </span>
          )}
        </div>
        {item.caption && <p className="text-gray-400 mb-6">{item.caption}</p>}
      </div>
      <div className="grid grid-cols-2 gap-4 px-6 pb-6">
        {item.secondary_image_url && (
          <div>
            <p className="text-sm text-gray-500 mb-2 font-medium">BEFORE</p>
            <div className="w-full h-64 rounded-lg overflow-hidden">
              <GalleryImg src={item.secondary_image_url} alt={`${item.title} before`} />
            </div>
          </div>
        )}
        <div className={item.secondary_image_url ? '' : 'col-span-2'}>
          <p className="text-sm text-gray-500 mb-2 font-medium">{item.secondary_image_url ? 'AFTER' : 'PHOTO'}</p>
          <div className="w-full h-64 rounded-lg overflow-hidden">
            <GalleryImg src={item.image_url} alt={`${item.title} after`} />
          </div>
        </div>
      </div>
    </div>
  );
}

function GridCard({ item }: { item: GalleryItem }) {
  return (
    <div className="bg-[#1A1A1A] rounded-lg overflow-hidden border border-gray-800 hover:border-red-900 transition-all group">
      <div className="aspect-square overflow-hidden w-full">
        <GalleryImg
          src={item.image_url}
          alt={item.title}
          className="group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <div className="p-5">
        <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
        {item.caption && <p className="text-gray-400 text-sm">{item.caption}</p>}
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

type TabType = 'repairs' | 'controllers' | 'strange';

function GalleryPage() {
  const [activeTab, setActiveTab] = useState<TabType>('repairs');
  const [items, setItems] = useState<GalleryItem[]>([
    ...FALLBACK_REPAIRS,
    ...FALLBACK_CONTROLLERS,
    ...FALLBACK_STRANGE,
  ]);

  useEffect(() => {
    fetchGalleryItems()
      .then(fetched => {
        if (fetched.length > 0) setItems(fetched);
      })
      .catch(() => { /* keep fallback */ });
  }, []);

  const repairs     = items.filter(i => i.category === 'repairs').sort((a, b) => a.sort_order - b.sort_order);
  const controllers = items.filter(i => i.category === 'controllers').sort((a, b) => a.sort_order - b.sort_order);
  const strange     = items.filter(i => i.category === 'strange').sort((a, b) => a.sort_order - b.sort_order);

  const TAB_ACTIVE = 'bg-red-600 text-white';
  const TAB_IDLE   = 'text-gray-400 hover:text-white';

  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Work</h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Browse our repairs and custom controller builds. Quality craftsmanship in every project.
          </p>
        </div>

        {/* Tab bar */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-[#1A1A1A] rounded-lg p-1 border border-gray-800">
            {(['repairs', 'controllers', 'strange'] as TabType[]).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-8 py-3 rounded-md font-semibold transition-all ${activeTab === tab ? TAB_ACTIVE : TAB_IDLE}`}
              >
                {tab === 'repairs' ? 'Repairs' : tab === 'controllers' ? 'Custom Controllers' : 'Strange'}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        {activeTab === 'repairs' && (
          <div className="grid md:grid-cols-2 gap-8">
            {repairs.map(item => <RepairCard key={item.id} item={item} />)}
          </div>
        )}

        {activeTab === 'controllers' && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {controllers.map(item => <GridCard key={item.id} item={item} />)}
          </div>
        )}

        {activeTab === 'strange' && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {strange.map(item => <GridCard key={item.id} item={item} />)}
          </div>
        )}
      </div>
    </div>
  );
}

export default GalleryPage;
